import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { apiRequest } from "../utils/api";
import { useAuthContext } from "./AuthContext";

const ChatUnreadContext = createContext();

export const useChatUnread = () => {
  return useContext(ChatUnreadContext);
};

export const ChatUnreadProvider = ({ children }) => {
  const { user, loading } = useAuthContext();

  const [unreadCount, setUnreadCount] = useState(0);

  const pollingRef = useRef(null);
  const stoppedByUnauthorizedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const isAuthenticated = !!user?.id;

  const stopPollingTimer = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const isUnauthorizedError = useCallback((err) => {
    return (
      err?.status === 401 ||
      err?.response?.status === 401 ||
      err?.message?.includes?.("401") ||
      err?.message?.includes?.("Unauthorized") ||
      err?.message?.includes?.("再ログインが必要です")
    );
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (loading) return;
    if (!isAuthenticated) return;
    if (stoppedByUnauthorizedRef.current) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      const data = await apiRequest("inventory/global-chat-messages/unread_count/");
      const count = data?.unread_count ?? 0;
      setUnreadCount(count);
    } catch (err) {
      if (err?.name === "AbortError") return;

      console.error("未読取得失敗:", err);

      if (isUnauthorizedError(err)) {
        stoppedByUnauthorizedRef.current = true;
        setUnreadCount(0);
        stopPollingTimer();
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [loading, isAuthenticated, isUnauthorizedError, stopPollingTimer]);

  const scheduleNextPolling = useCallback(() => {
    stopPollingTimer();

    if (loading) return;
    if (!isAuthenticated) return;
    if (stoppedByUnauthorizedRef.current) return;

    pollingRef.current = setTimeout(async () => {
      await fetchUnreadCount();
      scheduleNextPolling();
    }, 15000);
  }, [loading, isAuthenticated, fetchUnreadCount, stopPollingTimer]);

  useEffect(() => {
    stopPollingTimer();

    if (loading) return;

    if (!isAuthenticated) {
      stoppedByUnauthorizedRef.current = false;
      setUnreadCount(0);
      return;
    }

    stoppedByUnauthorizedRef.current = false;

    (async () => {
      await fetchUnreadCount();
      scheduleNextPolling();
    })();

    return () => {
      stopPollingTimer();
    };
  }, [loading, isAuthenticated, fetchUnreadCount, scheduleNextPolling, stopPollingTimer]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      if (loading) return;
      if (!isAuthenticated) return;
      if (stoppedByUnauthorizedRef.current) return;
      if (isFetchingRef.current) return;

      await fetchUnreadCount();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, isAuthenticated, fetchUnreadCount]);

  return (
    <ChatUnreadContext.Provider
      value={{
        unreadCount,
        setUnreadCount,
        refreshUnread: fetchUnreadCount,
      }}
    >
      {children}
    </ChatUnreadContext.Provider>
  );
};